// usePlayerAssignment.js
// Custom hook to manage player assignment, roster capacity, and modals
// Consolidates: position assignment, roster capacity, add player logic

import { useState, useCallback, useEffect } from 'react';
// 9o.6.d-followup: useNavigate removed — both single-add + bulk-add post-add navigates
// dropped (restored pre-Sep-2025 + mobile-parity behavior). Note: this hook's exec/bulk
// functions are dead code per 9o.6.d Catch #2 (production wraps them locally), but cleanup
// for consistency.
import { leaguesAPI } from '../../../../services/apiService';
import { 
  analyzeRosterCapacity, 
  canAddPlayers,
  findBestPositionForPlayer
} from '../../../../utils/RosterCapacityUtils';

/**
 * Custom hook for managing player assignment and roster capacity
 * Consolidates 6+ state variables into one organized hook
 * 
 * @param {string} leagueId - League UUID
 * @param {Object} league - League configuration object
 * @param {Object} options - Options (transactionsEnabled, leagueStatus, commissioner mode)
 * @returns {Object} Player assignment state and functions
 */
export const usePlayerAssignment = (leagueId, league, options = {}) => {
  const {
    transactionsEnabled = false,
    leagueStatus = 'setup',
    isCommissionerMode = false,
    activeTeamName = '',
    getTargetTeamId = () => null,
    user = null
  } = options;

  // Modal State
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [showBulkAssignmentModal, setShowBulkAssignmentModal] = useState(false);
  const [playerForAssignment, setPlayerForAssignment] = useState(null);
  
  // Roster State
  const [currentRoster, setCurrentRoster] = useState([]);
  const [rosterCapacityAnalysis, setRosterCapacityAnalysis] = useState(null);
  
  // UI State
  const [addingPlayer, setAddingPlayer] = useState(null);

  /**
   * Load current roster and analyze capacity
   */
  const loadRosterCapacity = useCallback(async () => {
    try {
      let rosterResponse;
      if (isCommissionerMode) {
        const targetTeamId = getTargetTeamId(null);
        if (targetTeamId) {
          rosterResponse = await leaguesAPI.getTeamRosterCanonical(leagueId, targetTeamId);
        } else {
          rosterResponse = await leaguesAPI.getMyRosterCanonical(leagueId);
        }
      } else {
        rosterResponse = await leaguesAPI.getMyRosterCanonical(leagueId);
      }

      const players = rosterResponse?.players || [];
      if (players.length > 0 || rosterResponse?.success) {
        // Filter out former/inactive players — they have accrued stats but aren't on the roster
        const activePlayers = players.filter(p => {
          const status = p.league_context?.roster?.status;
          return status && status !== 'former' && status !== 'inactive';
        });
        setCurrentRoster(activePlayers);
        const analysis = analyzeRosterCapacity(league, activePlayers);
        setRosterCapacityAnalysis(analysis);
        return analysis;
      } else {
        console.error('Failed to load roster for capacity analysis');
      }
    } catch (err) {
      console.error('Error loading roster capacity:', err);
    }
    return null;
  }, [leagueId, league, isCommissionerMode, getTargetTeamId]);

  /**
   * Handle add player - shows position modal after validation
   */
  const handleAddPlayer = useCallback(async (player, setError) => {
    // Check if transactions are allowed
    const isOfflineDraft = leagueStatus === 'offline_draft';
    const isDraftMode = ['draft', 'offline_draft'].includes(leagueStatus);
    
    if (!isOfflineDraft && !isDraftMode && !isCommissionerMode && !transactionsEnabled) {
      setError('Transactions are not allowed until the commissioner sets player prices');
      setTimeout(() => setError(''), 5000);
      return;
    }

    // Check ownership
    const isOwned = player.league_context?.team?.team_name;
    if (isOwned) {
      setError('Player is already owned by a team');
      setTimeout(() => setError(''), 5000);
      return;
    }

    // Always refetch — capacity may have changed since last load (e.g., user
    // just put someone on DL in another tab). loadRosterCapacity returns the
    // freshly computed analysis so we don't read a stale rosterCapacityAnalysis
    // in this same render.
    const freshAnalysis = await loadRosterCapacity();
    const analysis = freshAnalysis || rosterCapacityAnalysis;
    if (!analysis) {
      setError('Could not load roster capacity. Please try again.');
      setTimeout(() => setError(''), 5000);
      return;
    }

    const capacityCheck = canAddPlayers(1, analysis);
    if (!capacityCheck.canAdd) {
      setError(`Cannot add player: ${capacityCheck.message}`);
      setTimeout(() => setError(''), 5000);
      return;
    }

    const suggestion = findBestPositionForPlayer(player, analysis, league);
    
    if (!suggestion.hasSuggestions) {
      const firstName = player.info?.first_name;
      const lastName = player.info?.last_name;
      setError(`No available roster slots for ${firstName} ${lastName}`);
      setTimeout(() => setError(''), 5000);
      return;
    }

    // Show position assignment modal
    setPlayerForAssignment(player);
    setShowPositionModal(true);
  }, [transactionsEnabled, leagueStatus, isCommissionerMode, rosterCapacityAnalysis, league, loadRosterCapacity]);

  /**
   * Execute player assignment after position selection
   */
  const executePlayerAssignment = useCallback(async (player, assignment, callbacks = {}) => {
    // 9o.6.d: setError removed from destructure — never used in this function (all error paths
    // call onError, which callers wire to their own setError). Was a banked warning since pre-9o.
    const { onSuccess, onError, onPlayerAdded } = callbacks;
    const firstName = player.info?.first_name;
    const lastName = player.info?.last_name;
    const leaguePlayerId = player.ids?.league_player;

    setAddingPlayer(leaguePlayerId);

    try {
      // Bible Rule 9: missing required data is a bug — throw, not fallback.
      if (!leaguePlayerId) {
        throw new Error(
          `Cannot add player: missing ids.league_player (mlb=${player.ids?.mlb}, ` +
          `name=${player.info?.full_name}). Player object likely came from non-canonical source.`
        );
      }
      if (!assignment?.type) {
        throw new Error('Cannot add player: assignment.type is required');
      }
      if (assignment.type === 'active' && !assignment.slotId) {
        throw new Error(
          `Cannot add ${player.info?.full_name} to active roster: missing assignment.slotId`
        );
      }

      // 4-branch contract resolution per Bible § 4.5 contract model:
      //   - Minors: zero out
      //   - Offseason w/ projected_contract (line 1027): keeper rights
      //   - Owned in-season: existing roster_data.financial
      //   - FA pickup (default): baseline_price + league policy (§ 4.5.1 line 809, § 4.5.2 line 821)
      const isOffseason = ['setup', 'draft', 'finalizing'].includes(leagueStatus);
      const isOwnedAlready = player.league_context?.status === 'owned';
      const isMinors = assignment.type === 'minors';
      const playerName = player.info?.full_name;

      let playerSalary;
      let contractYears;
      if (isMinors) {
        playerSalary = 0;
        contractYears = 0;
      } else if (isOffseason && player.projected_contract) {
        playerSalary = player.projected_contract.baseline_price;
        contractYears = player.projected_contract.contract_years;
        if (playerSalary == null) {
          throw new Error(`Cannot add ${playerName}: missing projected_contract.baseline_price`);
        }
        if (contractYears == null) {
          throw new Error(`Cannot add ${playerName}: missing projected_contract.contract_years`);
        }
      } else if (isOwnedAlready) {
        playerSalary = player.league_context.financial?.contract_salary;
        contractYears = player.league_context.financial?.contract_years;
        if (playerSalary == null) {
          throw new Error(`Cannot add ${playerName}: missing league_context.financial.contract_salary on owned player`);
        }
        if (contractYears == null) {
          throw new Error(`Cannot add ${playerName}: missing league_context.financial.contract_years on owned player`);
        }
      } else {
        // Bible § 4.5.1 line 809 + § 10 line 1701: FA pickup price = baseline_price.
        // Bible § 4.5.2 line 821: contract_years = league.salary_settings.standard_contract_length.
        playerSalary = player.league_context?.financial?.baseline_price;
        contractYears = league?.salary_settings?.standard_contract_length;
        if (playerSalary == null) {
          throw new Error(
            `Cannot add ${playerName}: missing baseline_price on league_context.financial. ` +
            `Pricing engine may not have run for this league.`
          );
        }
        if (contractYears == null) {
          throw new Error(
            `Cannot add ${playerName}: league.salary_settings.standard_contract_length is not configured.`
          );
        }
      }

      const playerData = {
        league_player_id: leaguePlayerId,
        salary: playerSalary,
        contract_years: contractYears,
        roster_status: assignment.type,
        roster_position: assignment.type === 'active' ? assignment.slotId : null,
        start_contract: !isMinors
      };
      
      // Add commissioner action if needed
      if (isCommissionerMode) {
        const targetId = getTargetTeamId(null);
        playerData.commissioner_action = true;
        playerData.target_team_id = targetId;
      }

      const response = await leaguesAPI.addPlayerToTeam(leagueId, playerData);

      if (response.success) {
        const teamName = isCommissionerMode ? activeTeamName : 'your team';
        const positionText = assignment.type === 'active' ? assignment.position.toUpperCase() : assignment.type;
        
        if (onSuccess) {
          onSuccess(`${response.player_name || firstName + ' ' + lastName} added to ${teamName} (${positionText})!`);
        }
        
        // Refresh roster capacity
        await loadRosterCapacity();
        
        // Dispatch roster update event
        window.dispatchEvent(new Event('roster-updated'));
        
        if (onPlayerAdded) {
          onPlayerAdded(player);
        }
        // 9o.6.d-followup: removed setTimeout(navigate, 1000) → /leagues/{id} my-roster.
        // Pre-Sep-2025 behavior + mobile parity: stay on FA page after add. The success message
        // + roster-updated event + loadRosterCapacity provide adequate confirmation.

      } else {
        const errorMsg = response.message || response.error?.message || response.error || 'Failed to add player';
        if (onError) {
          onError(errorMsg);
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 
                      err.response?.data?.error?.message || 
                      err.response?.data?.error ||
                      err.message || 
                      'Failed to add player';
      if (onError) {
        onError(errorMsg);
      }
    } finally {
      setAddingPlayer(null);
      setShowPositionModal(false);
      setPlayerForAssignment(null);
    }
  }, [isCommissionerMode, activeTeamName, getTargetTeamId, leagueId, loadRosterCapacity]);

  /**
   * Handle batch add players - shows bulk assignment modal
   */
  const handleBatchAddPlayers = useCallback(async (selectedPlayers, setError) => {
    if (!transactionsEnabled) {
      setError('Transactions are not allowed until prices are set');
      return;
    }

    if (selectedPlayers.length === 0) {
      setError('No players selected');
      return;
    }

    if (!rosterCapacityAnalysis) {
      await loadRosterCapacity();
      setError('Checking roster capacity...');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const capacityCheck = canAddPlayers(selectedPlayers.length, rosterCapacityAnalysis);
    if (!capacityCheck.canAdd) {
      setError(`Cannot add ${selectedPlayers.length} players: ${capacityCheck.message}`);
      setTimeout(() => setError(''), 8000);
      return;
    }

    setShowBulkAssignmentModal(true);
  }, [transactionsEnabled, rosterCapacityAnalysis, loadRosterCapacity]);

  /**
   * Handle bulk assignment completion
   */
  const handleBulkAssignmentComplete = useCallback(async (assignmentData, callbacks = {}) => {
    const { clearAllSelections, onSuccess, onError } = callbacks;
    
    const results = {
      successful: [],
      failed: []
    };

    try {
      for (const assignment of assignmentData) {
        try {
          const leaguePlayerId = assignment.player.ids?.league_player;
          const playerData = {
            league_player_id: leaguePlayerId,
            salary: assignment.salary,
            contract_years: assignment.contract_years,
            roster_status: assignment.roster_status,
            roster_position: assignment.roster_position,
            start_contract: assignment.start_contract
          };

          if (isCommissionerMode) {
            playerData.commissioner_action = true;
            playerData.target_team_id = getTargetTeamId(null);
          }

          const response = await leaguesAPI.addPlayerToTeam(leagueId, playerData);

          if (response.success) {
            const firstName = assignment.player.info?.first_name;
            const lastName = assignment.player.info?.last_name;
            results.successful.push({
              player: assignment.player,
              name: response.player_name || `${firstName} ${lastName}`,
              position: assignment.roster_status === 'active' ? assignment.roster_position : assignment.roster_status
            });
          } else {
            const firstName = assignment.player.info?.first_name;
            const lastName = assignment.player.info?.last_name;
            results.failed.push({
              player: assignment.player,
              name: `${firstName} ${lastName}`,
              error: response.message || 'Unknown error'
            });
          }
        } catch (playerErr) {
          const firstName = assignment.player.info?.first_name;
          const lastName = assignment.player.info?.last_name;
          results.failed.push({
            player: assignment.player,
            name: `${firstName} ${lastName}`,
            error: playerErr.message || 'Network error'
          });
        }
      }

      if (results.successful.length > 0) {
        const teamName = isCommissionerMode ? activeTeamName : 'your team';
        if (onSuccess) {
          onSuccess(`Successfully added ${results.successful.length} players to ${teamName}!`);
        }
        
        if (clearAllSelections) {
          clearAllSelections();
        }
        
        await loadRosterCapacity();
        window.dispatchEvent(new Event('roster-updated'));

        // 9o.6.d-followup (bulk variant): removed navigate → /leagues/{id} my-roster.
        // Same rationale as single-add: stay on FA page after bulk add. (This entire function
        // is dead code per 9o.6.d Catch #2 — production wraps it locally — but cleanup
        // for consistency.)
      }

      if (results.failed.length > 0) {
        const failedNames = results.failed.map(f => f.name).join(', ');
        if (onError) {
          onError(`Failed to add: ${failedNames}`);
        }
      }

    } catch (err) {
      console.error('Error in bulk assignment completion:', err);
      if (onError) {
        onError('Bulk assignment failed');
      }
    } finally {
      setShowBulkAssignmentModal(false);
    }
  }, [isCommissionerMode, activeTeamName, getTargetTeamId, leagueId, loadRosterCapacity]);

  /**
   * Initialize roster capacity on mount
   */
  useEffect(() => {
    if (leagueId && league) {
      loadRosterCapacity();
    }
  }, [leagueId, league, loadRosterCapacity]);

  /**
   * Listen for roster-updated events from other components (e.g., MyRoster
   * move/DL/drop). Keeps currentRoster fresh without requiring a remount.
   */
  useEffect(() => {
    const handleRosterUpdated = () => {
      if (leagueId && league) {
        loadRosterCapacity();
      }
    };
    window.addEventListener('roster-updated', handleRosterUpdated);
    return () => window.removeEventListener('roster-updated', handleRosterUpdated);
  }, [leagueId, league, loadRosterCapacity]);

  return {
    // Modal State
    showPositionModal,
    setShowPositionModal,
    showBulkAssignmentModal,
    setShowBulkAssignmentModal,
    playerForAssignment,
    setPlayerForAssignment,
    
    // Roster State
    currentRoster,
    rosterCapacityAnalysis,
    
    // UI State
    addingPlayer,
    setAddingPlayer,

    // Functions
    loadRosterCapacity,
    handleAddPlayer,
    executePlayerAssignment,
    handleBatchAddPlayers,
    handleBulkAssignmentComplete
  };
};
